import { Slider, Text, Flex, Stack, Title, Container, Box } from '@mantine/core';
import { useParams } from 'react-router-dom';
import Breadcrumb from '@components/breadcrumb';
import NextNav from '@components/next-nav';
import {
  FIND_VOYAGE_VIBE_STATES,
  FIND_VOYAGE_VIBE_STATES_EXPANDED,
  UPSERT_VOYAGE_VIBE_STATES,
  VOYAGE_VIBE_RESULTS,
} from '@queries/voyage-vibe-state';
import LoadingData from '@components/graphql-loading-data';
import GraphqlError from '@components/graphql-error';
import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { VoyageVibeStateInput } from '@generated/graphql';
import classes from './survey-vibe-travel-style.module.css';

type UpdatedSliderStates = {
  [key: string]: {
    id: string | undefined;
    title: string;
    rating: number;
  };
};

const questions = [
  { id: '901', label: 'Interaction' },
  { id: '902', label: 'Activity' },
  { id: '903', label: 'Learning' },
];
const marks = [
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 5, label: '5' },
];

function TravelStyle() {
  const { clientId, tripProfileId } = useParams();
  const [sliderStates, setSliderStates] = useState<UpdatedSliderStates>({});
  const [doneState, setDoneState] = useState(false);

  if (!clientId || !tripProfileId) return null;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { loading, error, data } = useQuery(FIND_VOYAGE_VIBE_STATES, {
    variables: { tripProfileId, specificQuestionIds: questions.map((q) => q.id) },
  });
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [upsertVoyageVibeStates, mutationState] = useMutation(UPSERT_VOYAGE_VIBE_STATES, {
    // FIND_VOYAGE_VIBE_STATES_EXPANDED
    // refetchQueries: ['findVoyageVibeResults'],
    refetchQueries: [
      { query: VOYAGE_VIBE_RESULTS, variables: { tripProfileId } },
      {
        query: FIND_VOYAGE_VIBE_STATES_EXPANDED,
        variables: { tripProfileId, specificQuestionIds: ['200'] },
      },
      {
        query: FIND_VOYAGE_VIBE_STATES,
        variables: { tripProfileId, specificQuestionIds: questions.map((q) => q.id) },
      },
    ],
    awaitRefetchQueries: true,
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (data) {
      const { findVoyageVibeStates } = data;

      // Update state based on the received data
      setSliderStates(() => {
        const updatedSliderStates: UpdatedSliderStates = {};
        questions.forEach((question) => {
          const record = findVoyageVibeStates.find((s) => s.questionId === question.id);
          // TODO:  the model for voyagerVibeState needs to have a relationship to questions, without where are potential for errors
          updatedSliderStates[question.id] = {
            id: record?.id,
            title: question.label, // this may collide with what was saved
            rating: record?.rating || 0,
          };
        });
        const areAllRatingsValid = Object.values(updatedSliderStates).every((state) => state.rating > 0);
        setDoneState(areAllRatingsValid);

        return updatedSliderStates;
      });
    }
  }, [data]);

  if (loading || mutationState.loading) return <LoadingData />;
  if (error) return <GraphqlError error={error} />;
  if (mutationState.error) return <GraphqlError error={mutationState.error} />;

  const handleSliderChange = (questionId: string, value: number) => {
    setSliderStates((prevStates) => {
      const updatedStates = {
        ...prevStates,
        [questionId]: { ...prevStates[questionId], rating: value },
      };

      const areAllRatingsValid = Object.values(updatedStates).every((state) => state.rating > 0);
      setDoneState(areAllRatingsValid);

      return updatedStates;
    });
  };

  const save = async () => {
    const vibeStates: VoyageVibeStateInput[] = [];
    Object.keys(sliderStates).forEach((questionId) => {
      const state = sliderStates[questionId];
      vibeStates.push({
        id: state.id || undefined,
        tripProfileId,
        questionId,
        title: state.title,
        rating: state.rating,
      });
    });
    await upsertVoyageVibeStates({
      variables: {
        voyageVibeStates: vibeStates,
      },
    });
  };

  return (
    <Container fluid>
      <Breadcrumb pageStep={4} />
      <Box bg="var(--mantine-color-voyTitleBox-6)" p={10} mb={30} mt={20}>
        <Title order={2} c="white" ta="center" className={classes.title}>
          Voyage Vibe - Style
        </Title>
      </Box>

      <Stack align="center">
        <Title order={3} pt={40} className={classes.directions}>
          Please rate each travel style. 1 = lowest to 5 = highest
        </Title>

        <Stack h={400} w={600} gap={70}>
          {questions.map((question) => (
            <Flex key={question.id} direction="row" gap="md" justify="flex-end" align="center">
              <Text>{question.label}</Text>
              <Slider
                w={400}
                size="xl"
                min={1}
                max={5}
                marks={marks}
                step={1}
                value={sliderStates[question.id]?.rating || 0}
                onChange={(value) => handleSliderChange(question.id, value)}
              />
            </Flex>
          ))}
        </Stack>
      </Stack>
      <NextNav
        next={`/clients/${clientId}/trip-profiles/${tripProfileId}/vibe-results`}
        saveFunction={save}
        nextDisabled={!doneState}
        saveDisabled={!doneState}
      />
    </Container>
  );
}

export default TravelStyle;
